/**
 * StandaloneBootstrap — Runs AngularJS-equivalent boot-time API calls in standalone mode.
 *
 * In embedded mode, the AngularJS `index` controller loads 15+ global config values
 * (user info, image prefix, help URL, workflow transitions, etc.) during `activate()`.
 * In standalone mode, this component replicates those calls so the React shell has
 * all the same global state available.
 *
 * Source: public/index.js lines 389-525 (activate() function)
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { getMountConfig } from '@/mountConfig';
import { axiosClient } from '@/lib/axiosClient';
import { buildApiBase } from '@/lib/buildApiBase';
import { useLegacyPermissions } from '@/lib/useLegacyPermissions';
import { useAppGlobalsStore } from '@/store/appGlobalsStore';
import { deCompressLanguagePack, setLanguagePack } from '@/features/coreScript/coreScriptApi';
import { StandaloneSpinner } from '@/layout/StandaloneSpinner';

const API_BASE = buildApiBase();

interface StandaloneBootstrapProps {
  children: ReactNode;
}

/**
 * Boot-time API calls that match the AngularJS index controller's activate() function.
 * Only runs in standalone mode (embedded mode gets these from AngularJS).
 *
 * Render gate: children render immediately UNLESS we need to fetch the
 * language pack on first load — in that case rendering is blocked on the
 * fetch so CoreScript routes (~30 of them) never mount with an empty
 * translation map. Cached pack on subsequent loads is read synchronously
 * from localStorage, so the gate is a one-time cost per user.
 */
export function StandaloneBootstrap({ children }: StandaloneBootstrapProps) {
  const { mode } = getMountConfig();
  const perms = useLegacyPermissions();
  const hasBooted = useRef(false);
  // Default true so non-standalone or non-SGStaff sessions render immediately.
  // Set to false below only when the language pack actually needs fetching.
  const [isLanguagePackReady, setIsLanguagePackReady] = useState(true);

  useEffect(() => {
    if (mode !== 'standalone') return;
    if (hasBooted.current) return;
    if (!perms.SGStaff) return; // Wait for permissions to load

    hasBooted.current = true;

    // All calls fire in parallel (matches AngularJS which fires them sequentially but doesn't await)

    const globals = useAppGlobalsStore.getState();

    // ── 1. Load current user (index.js lines 391-401) ──
    axiosClient
      .get(`${API_BASE}/API/Partial/GetCurrentUsername`)
      .then((res) => {
        const username = res.data;
        if (username) {
          globals.setCurrentUser(username);
          (window as any).currentuser = username;
          try {
            localStorage.setItem('currentUser', username);
          } catch {
            // quota exceeded
          }
        }
      })
      .catch(() => {});

    // ── 2. Load image prefix (index.js lines 404-409) ──
    axiosClient
      .get(`${API_BASE}/API/Partial/GetImageViewerSrcPrefix`, { silent401: true } as any)
      .then((res) => {
        if (res.data) {
          globals.setImagePrefix(res.data);
          (window as any).imageprefix = res.data;
        }
      })
      .catch(() => {});

    // ── 3. Load accept-all source systems (index.js lines 423-427) ──
    //    AngularJS returns a comma-separated string; split into array
    axiosClient
      .get(`${API_BASE}/API/Partial/GetAcceptAllSourceSystems`)
      .then((res) => {
        if (typeof res.data === 'string') {
          globals.setAcceptAllSourceSystems(res.data.split(','));
        } else if (Array.isArray(res.data)) {
          globals.setAcceptAllSourceSystems(res.data);
        }
      })
      .catch(() => {});

    // ── 4. Load max date questions (index.js lines 429-433) ──
    //    AngularJS returns a comma-separated string; split into array
    axiosClient
      .get(`${API_BASE}/API/Partial/getMaxDateCurrent`)
      .then((res) => {
        if (typeof res.data === 'string') {
          globals.setMaxDateQuestions(res.data.split(','));
        } else if (Array.isArray(res.data)) {
          globals.setMaxDateQuestions(res.data);
        }
      })
      .catch(() => {});

    // ── 5. Load Phase V only permissions (index.js lines 435-439) ──
    //    AngularJS returns a comma-separated string; split + trim
    axiosClient
      .get(`${API_BASE}/API/Partial/GetPhaseVOnlyPermissions`)
      .then((res) => {
        if (typeof res.data === 'string') {
          const arr = res.data
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);
          globals.setPhaseVOnlyPermissions(arr);
        } else if (Array.isArray(res.data)) {
          const arr = res.data.map((s: string) => s?.trim()).filter(Boolean);
          globals.setPhaseVOnlyPermissions(arr);
        }
      })
      .catch(() => {});

    // ── 6. Load workflow transitions (index.js lines 441-443) ──
    //    Param name must be `WorkFlowName` (matches workflowDataService.js:149)
    axiosClient
      .get(`${API_BASE}/API/Workflow/GetWorkflowAllowedStateTransition`, {
        params: { WorkFlowName: 'WorkOrder' },
      })
      .then((res) => {
        if (res.data) {
          globals.setAllowedWorkOrderTransitions(res.data);
        }
      })
      .catch(() => {});

    // ── 7. Load task workflow state codes (index.js lines 445-449) ──
    //    AngularJS uses webconfigDataService.getWorkFlowStateCodesForTasks()
    //    which also returns a comma-separated string — split into array
    axiosClient
      .get(`${API_BASE}/API/WorkOrderInfo/GetWorkflowStateCodesForScheduling/`)
      .then((res) => {
        if (typeof res.data === 'string') {
          (window as any).tasksWFSCodes = res.data.split(',');
        } else if (Array.isArray(res.data)) {
          (window as any).tasksWFSCodes = res.data;
        }
      })
      .catch(() => {});

    // ── 8. Load build info (index.js lines 411-415) ──
    //    URL: /API/BuildInfo/Get/ (matches generalDataService.js:261)
    axiosClient
      .get(`${API_BASE}/API/BuildInfo/Get/`)
      .then((res) => {
        if (res.data) {
          globals.setBuildInfo(typeof res.data === 'string' ? res.data : JSON.stringify(res.data));
          (window as any).buildInfo = res.data;
        }
      })
      .catch(() => {});

    // ── 9. Load help URL (index.js lines 417-421) ──
    axiosClient
      .get(`${API_BASE}/API/Partial/GetHelpURL/`)
      .then((res) => {
        if (res.data) {
          globals.setHelpUrl(String(res.data));
          (window as any).helpUrl = res.data;
        }
      })
      .catch(() => {});

    // ── 10. Load feedback email (index.js lines 451-455) ──
    axiosClient
      .get(`${API_BASE}/API/Partial/GetFeedbackEmail/`)
      .then((res) => {
        if (res.data) {
          globals.setFeedbackEmail(String(res.data));
          (window as any).feedbackEmail = res.data;
        }
      })
      .catch(() => {});

    // ── 9. Load/initialize user settings + theme (index.js lines 492-523) ──
    try {
      const settingsStr = localStorage.getItem('userSettings');
      if (!settingsStr) {
        const defaults = {
          enableBackButtons: true,
          enableRecentOrders: true,
          theme: 'default',
          primary: '#0075d6',
          secondary: '#53b50a',
          loadingSpinner: 'safeview.gif',
          showAllResults: true,
          rebrand: true,
        };
        localStorage.setItem('userSettings', JSON.stringify(defaults));
        globals.setSettings(defaults);
      } else {
        const settings = JSON.parse(settingsStr);
        globals.setSettings(settings);
      }
    } catch {
      // corrupted localStorage
    }
  }, [mode, perms.SGStaff]);

  // ── Language pack (separate effect; NOT gated on perms.SGStaff or mode) ──
  //
  // The language pack populates CoreScript form labels (group titles, field
  // labels, station names). Every CoreScript route reads it synchronously via
  // `deCompressLanguagePack()`, which serves an in-memory cache that is
  // rehydrated from a gzipped localStorage copy on reload (see setLanguagePack /
  // persistLanguagePack in coreScriptApi.ts).
  //
  // Why no gates:
  //   - SGStaff: the AngularJS controller at public/index.js:499 also gates on
  //     SGStaff, leaving non-staff users with raw keys in either shell. Dropping
  //     the gate here fixes that.
  //   - mode='standalone': dropping this lets standalone OR embedded sessions
  //     both populate the cache from React, independent of the AngularJS
  //     controller's behavior.
  //
  // Caching model (restores AngularJS parity — public/index.js on master_7_23_26):
  //   `setLanguagePack()` stores the pack in-memory AND persists it gzipped
  //   (~1.5 MB, under the ~5 MB quota) to localStorage. On the next reload the
  //   gzipped copy rehydrates synchronously, so we render immediately and only
  //   refresh in the background — no blocking fetch. The old "in-memory only"
  //   approach re-fetched the full pack and blocked render on EVERY reload,
  //   which was the source of the reported slowness.
  //
  // Idempotent: if `deCompressLanguagePack()` already returns a populated map
  // (localStorage gzipped copy, or this effect ran earlier in the session),
  // skip the blocking fetch and refresh in the background instead.
  const languagePackFetched = useRef(false);
  useEffect(() => {
    if (languagePackFetched.current) return;

    // VendorConnect users never hit CoreScript routes — skip the ~1.5 MB
    // language-pack fetch (and the safeview.gif spinner it blocks rendering
    // behind) so the vendor portal loads without a staff-branded overlay.
    // Mirrors the vendor-route gate in useLegacyPermissionsImpl.tsx.
    const isVendorRoute =
      (window.location.hash || '').includes('/VendorConnect') ||
      (window.location.hash || '').includes('/vendor-connect') ||
      sessionStorage.getItem('vc_session_active') === '1' ||
      sessionStorage.getItem('vc_auth_pending') === 'vendor';
    if (isVendorRoute) {
      languagePackFetched.current = true;
      return;
    }

    // Fetch → normalize → cache (in-memory + gzipped localStorage via
    // setLanguagePack). Reused by both the cold (blocking) and warm (background)
    // paths below.
    const fetchAndCache = () =>
      axiosClient
        .get<{ keyValues?: Record<string, string> } | Record<string, string>>(
          `${API_BASE}/API/Script/GetLanguagePack`,
          // `silent: true` — this fetch runs on EVERY route (including /login)
          // because translations are needed by any authenticated user, not just
          // staff. Without the silent flag an unauthenticated visitor to /#/login
          // hits a 401 here and the axios interceptor immediately bounces to
          // Windows Auth / Okta, replacing the login page they were trying to
          // see. Silent suppresses both the 401 redirect and the 403 toast — the
          // .catch below already logs the error for diagnostics.
          { params: { language: 'EN' }, silent: true } as never
        )
        .then((res) => {
          if (!res.data) return;
          // AngularJS shape: { keyValues: { 'WorkOrder.Modify.Type': 'Type', ... } }.
          // Some envs return the map directly. Normalize both.
          const rawMap: Record<string, string> =
            (res.data as { keyValues?: Record<string, string> }).keyValues ??
            (res.data as Record<string, string>);

          // AngularJS lowercases keys before storing (public/index.js:497-498)
          // and `translate()` lowercases the lookup key (coreScriptEngine.ts:139).
          const translations: Record<string, string> = {};
          for (const property in rawMap) {
            if (typeof rawMap[property] === 'string') {
              translations[property.toLowerCase()] = rawMap[property];
            }
          }

          // Populate in-memory cache AND persist gzipped to localStorage so the
          // next full page reload rehydrates synchronously (no blocking fetch).
          setLanguagePack(translations);
        });

    let alreadyCached = false;
    try {
      alreadyCached = Object.keys(deCompressLanguagePack()).length > 0;
    } catch {
      alreadyCached = false;
    }

    if (alreadyCached) {
      languagePackFetched.current = true;
      // Warm path — a cached copy already rehydrated from localStorage, so render
      // immediately WITHOUT blocking, then refresh in the background so backend
      // translation updates land on the next load. Mirrors AngularJS
      // overwrite-on-bootstrap (public/index.js:497), minus the blocking. The
      // cached copy stays in use if the refresh fails.
      fetchAndCache().catch((err) => {
        console.error('[StandaloneBootstrap] background language pack refresh failed', err);
      });
      return;
    }

    // Cold path — no cached copy; block rendering until the pack lands so
    // CoreScript routes never mount with an empty translation map.
    languagePackFetched.current = true;
    setIsLanguagePackReady(false);

    fetchAndCache()
      .catch((err) => {
        console.error('[StandaloneBootstrap] language pack fetch failed', err);
      })
      .finally(() => {
        setIsLanguagePackReady(true);
      });
  }, []);

  if (!isLanguagePackReady) {
    return <StandaloneSpinner />;
  }

  return <>{children}</>;
}
